'use client';;
import { useRouter } from 'next/navigation';
import { PaginatedBlogPosts } from '../../types/blog';
import { Pagination } from '../ui/Pagination';
import Breadcrumb from '../ui/Breadcrumb';
import { BlogCard } from './BlogCard';
import { useTranslations } from 'next-intl';
import { ArticleIcon } from '../icons';
import styles from './Blog.module.css';

interface BlogListingProps {
  posts: PaginatedBlogPosts;
  searchParams: {
    page?: string;
    search?: string;
    tag?: string;
    author?: string;
  };
}

export function BlogListing({ posts, searchParams }: BlogListingProps) {
  const t = useTranslations('blog');
  const tCommon = useTranslations('common');
  const router = useRouter();

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams();
    if (searchParams.search) params.set('search', searchParams.search);
    if (searchParams.tag) params.set('tag', searchParams.tag);
    if (searchParams.author) params.set('author', searchParams.author);
    params.set('page', page.toString());
    
    router.push(`/blog?${params.toString()}`);
  };

  const clearFilters = () => {
    router.push('/blog');
  };

  const breadcrumbItems = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    { label: tCommon('breadcrumb.blog'), path: '/blog' }
  ];

  return (
    <div className={styles.blogListingPage}>
      <div className={styles.blogBreadcrumbBar}>
        <div className={styles.blogContainer}>
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      <div className={styles.blogHero}>
        <div className={styles.blogContainer}>
          <div className={styles.blogHeroInner}>
            <h1 className={styles.blogHeroTitle}>{t('hero.title')}</h1>
            <p className={styles.blogHeroSubtitle}>{t('hero.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className={`${styles.blogContainer} ${styles.blogMainContent}`}>
        <div className={styles.blogResultsInfo} />

        {posts.items.length > 0 ? (
          <div style={{ marginBottom: 48 }}>
            {posts.items.slice(0, 2).length > 0 && (
              <div className={styles.blogFeaturedGrid}>
                {posts.items.slice(0, 2).map((post) => (
                  <BlogCard key={post._id} post={post} variant="large" eagerImage />
                ))}
              </div>
            )}

            {posts.items.slice(2).length > 0 && (
              <div className={styles.blogRegularGrid}>
                {posts.items.slice(2).map((post) => (
                  <div key={post._id} className={styles.blogRegularItem}>
                    <BlogCard post={post} variant="small" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.blogEmptyState}>
            <div className={styles.blogEmptyIcon}>📝</div>
            <h2 className={styles.blogEmptyTitle}>No articles found</h2>
            <p className={styles.blogEmptyText}>
              {searchParams.search || searchParams.tag 
                ? 'Try adjusting your search or filter criteria'
                : 'Check back later for new content'
              }
            </p>
            {(searchParams.search || searchParams.tag) && (
              <button className={styles.blogClearButton} onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        )}

        {posts.totalPages > 1 && (
          <div className={styles.blogPaginationWrap}>
            <Pagination
              currentPage={posts.page}
              totalPages={posts.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
